import styled from "@emotion/styled"
import { FaTimes } from "@react-icons/all-files/fa/FaTimes"
import { Value } from "@stringtale/react"
import { discoveryModalOpenAtom } from "app/support/discoveryModalState"
import useDiscoveryTours from "app/support/hooks/useDiscoveryTours"
import { GetCurrentUserResult } from "app/users/queries/getCurrentUser"
import { useAtom } from "jotai"
import { DARK_BLUE_COLOR, HEADER_Z_INDEX, PX14 } from "theme/consts"
import Button from "ui/Button"
import Group from "ui/Group"
import Stack from "ui/Stack"
import Text from "ui/Text"
import { usePosthogWithCurrentUser } from "app/core/hooks/usePosthogWithCurrentUser"
import startViewTransition from "app/core/utils/startViewTransition"

const DiscoveryButtonBackground = styled(Group)`
  filter: drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.4));
  cursor: pointer;
  z-index: ${HEADER_Z_INDEX};
  height: 54px;
  border-radius: 10px;
  border: 1px solid ${DARK_BLUE_COLOR};
  position: relative;
  background-color: #8d23ff;
  box-shadow: inset 0px -5px 0px 0px #6314b8;
`

const CloseTriggerButton = styled(Button)`
  position: absolute;
  background-color: ${DARK_BLUE_COLOR};
  border-radius: 50px;
  color: white;
  top: -8px;
  right: -8px;
  height: 24px;
  width: 24px;
  padding: 0;
`

const IconContainer = styled.div`
  background-color: #6e00e6;
  height: 54px;
  width: 54px;
  min-height: 54px;
  min-width: 54px;
  flex-shrink: 0;
  flex-grow: 0;
  overflow: visible;
  border-radius: 9px 0 0 9px;
  box-shadow: inset 0px -5px 0px 0px #6314b8;
  align-items: center;
  justify-content: center;
  display: flex;
`

const ButtonBodyContent = styled(Stack)`
  padding: 0 8px;
  width: 171px;
  flex-grow: 0;
  flex-shrink: 0;
  align-items: flex-start;
  box-sizing: border-box;
`

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 12px;
  background-color: #e1d3f5;
  border: 1px solid ${DARK_BLUE_COLOR};
  border-radius: 6px;
  display: flex;
  overflow: hidden;
  position: relative;
  box-shadow: inset -5px 0px 0 #d2beef;
`

const ProgressFill = styled.div<{ percentage: number }>`
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 0%;
  background-color: #32ef6c;
  border-radius: 0 6px 6px 0;
  box-sizing: border-box;
  box-shadow: inset -5px 0px 0 #11b944;
  @keyframes fillProgress {
    from {
      width: 0%;
    }
    to {
      width: ${(props) => props.percentage}%;
    }
  }
  animation: fillProgress 1s ease-out forwards;
`

const SegmentDividers = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  display: flex;
  pointer-events: none;
`

const Segment = styled.div`
  flex: 1;
  border-right: 1px solid ${DARK_BLUE_COLOR};
  &:last-child {
    border-right: none;
  }
`

function ProgressBar({
  completed,
  total,
}: {
  completed: number
  total: number
}) {
  const segments = Array(8).fill(0)
  const percentage = (completed / total) * 100

  return (
    <ProgressBarContainer>
      <ProgressFill percentage={percentage} />
      <SegmentDividers>
        {segments.map((_, index) => (
          <Segment key={index} />
        ))}
      </SegmentDividers>
    </ProgressBarContainer>
  )
}

const DiscoveryButtonIcon = styled.img`
  width: 70px;
  height: 70px;
  top: -12px;
  left: -2px;
  z-index: 1;
  position: relative;
`

export default function DiscoveryButton({
  toggleDiscoveryButton,
  currentUser,
}: {
  toggleDiscoveryButton: (state: "OPENED" | "CLOSED" | "MINIMIZED") => void
  currentUser: GetCurrentUserResult
}) {
  const [discoveryModalOpen, setDiscoveryModalOpen] = useAtom(
    discoveryModalOpenAtom
  )
  const { discoveryTours, completedToursCount } = useDiscoveryTours({})
  const analytics = usePosthogWithCurrentUser(currentUser)

  return (
    <>
      {!discoveryModalOpen && discoveryTours.length > 0 && (
        <>
          <DiscoveryButtonBackground
            onClick={() => {
              analytics.capture({
                name: "discovery_modal_opened",
              })
              startViewTransition(() => {
                setDiscoveryModalOpen(true)
              })
            }}
            align="center"
            justify="flex-start"
            style={{
              viewTransitionName: "discovery",
            }}
          >
            <IconContainer>
              <DiscoveryButtonIcon src="/icons/support/discovery-superhero.png" />
            </IconContainer>
            <ButtonBodyContent gap={"2px"}>
              <Text size={PX14} bold color="white">
                <Value name="apps.web.src.core.components.discoverybutton.ontdek_123zing">
                  Ontdek 123ZING!
                </Value>
              </Text>
              <ProgressBar
                completed={completedToursCount}
                total={discoveryTours.length}
              />
            </ButtonBodyContent>

            <CloseTriggerButton
              type="button"
              onClick={async (e) => {
                e.stopPropagation()
                e.preventDefault()
                toggleDiscoveryButton("CLOSED")
              }}
            >
              <FaTimes size={14} />
            </CloseTriggerButton>
          </DiscoveryButtonBackground>
        </>
      )}
    </>
  )
}
